import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Autocomplete,
  Link,
  Loading,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import {
  ErrorMessage,
  InputWrapper,
} from '@elementary/common-ui/components/FormControls/FormControls';
import { Controller, useForm } from 'react-hook-form';
import useNango from 'shared/hooks/useNango';
import useSupportChat from 'shared/hooks/useSupportChat';
import {
  TicketingForm,
  TicketingIntegration,
  TicketingProject,
  TicketingTool,
} from 'shared/types';
import { IntegrationError } from 'settings/types';
import { useTicketingIntegration } from 'shared/hooks/integrations';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import JiraServiceAccountContent from './JiraServiceAccountContent';
import { ServiceNowAppConfig } from './ServiceNowAppConfigContent';

enum DialogStep {
  serviceAccountInfo,
  initial,
  auth,
  success,
  failure,
}

export type ToolDialogConfig = {
  title: string;
  onSubmit: () => void;
  isValid: boolean;
  saveButtonText?: string;
  reset: () => void;
  error?: IntegrationError;
  loading?: boolean;
};

type Props = {
  open: boolean;
  data?: TicketingIntegration;
  onSubmit: (data: TicketingForm) => void;
  error?: IntegrationError;
  loading?: boolean;
  envId: string;
  type: TicketingTool;
  appConfig?: ServiceNowAppConfig;
  onDialogProps: (config: ToolDialogConfig) => void;
};

const ToolDialogContent = ({
  open,
  data,
  onSubmit,
  error,
  loading,
  envId,
  type,
  appConfig,
  onDialogProps,
}: Props) => {
  const [step, setStep] = useState<DialogStep>(DialogStep.initial);
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();
  const supportChat = useSupportChat();

  const defaultValues = useMemo(
    () => ({
      projectId: data?.project_id,
      type,
      name: data?.name,
      id: data?.id,
    }),
    [data, type],
  );

  const { ticketingProjectsMutation } = useTicketingIntegration({ envId });
  const {
    mutate: getTicketingProjects,
    error: ticketingProjectsError,
    data: ticketingProjects,
    isLoading: ticketingProjectsLoading,
  } = ticketingProjectsMutation;

  useEffect(() => {
    if (nangoSuccess) {
      getTicketingProjects(type);
    }
  }, [getTicketingProjects, nangoSuccess, type]);

  useEffect(() => {
    if (!open) return;

    if (data?.enabled) {
      setStep(DialogStep.success);
      getTicketingProjects(type);
    } else if (type === 'jira') {
      setStep(DialogStep.serviceAccountInfo);
    } else {
      setStep(DialogStep.auth);
    }
  }, [data, getTicketingProjects, type, open]);

  useEffect(() => {
    if (step !== DialogStep.auth) return;

    if (type === 'servicenow' && appConfig) {
      nangoAuth(
        appConfig.configKey,
        undefined,
        { subdomain: appConfig.subdomain },
        envId,
      );
    } else {
      const providerKey = type === 'jira' ? 'jira-v2' : type;
      nangoAuth(providerKey, undefined, undefined, envId);
    }
  }, [step, nangoAuth, type, appConfig, envId]);

  useEffect(() => {
    if (nangoSuccess) setStep(DialogStep.success);
  }, [nangoSuccess]);

  useEffect(() => {
    if (nangoError) setStep(DialogStep.failure);
  }, [nangoError]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    register,
  } = useForm<TicketingForm>({ defaultValues });

  const resetForm = useCallback(
    () => reset(defaultValues),
    [defaultValues, reset],
  );

  const ticketingProjectsOptions = useMemo(
    () =>
      ticketingProjects?.projects?.reduce(
        (acc: Record<string, TicketingProject>, project: TicketingProject) => {
          acc[project.id] = project;
          return acc;
        },
        {},
      ) || {},
    [ticketingProjects],
  );

  const toolLabel = {
    jira: 'Jira',
    linear: 'Linear',
    servicenow: 'ServiceNow',
  }[type];
  const projectLabel = { jira: 'project', linear: 'team', servicenow: 'group' }[
    type
  ];

  const getProjectLabel = useCallback(
    (project: TicketingProject) =>
      project.key !== project.name
        ? `${project.key} - ${project.name}`
        : project.name,
    [],
  );

  const projectOptions = useMemo(
    () =>
      Object.values(ticketingProjectsOptions).map((project) => ({
        value: project.id,
        label: getProjectLabel(project),
      })),
    [ticketingProjectsOptions, getProjectLabel],
  );

  const getOptionLabel = useCallback(
    (option: { value: string; label: string } | string) => {
      if (typeof option === 'object') return option.label;
      const project = ticketingProjectsOptions[option];
      return project
        ? getProjectLabel(project)
        : `Loading ${projectLabel} name...`;
    },
    [ticketingProjectsOptions, projectLabel, getProjectLabel],
  );

  const filterOptions = useCallback(
    (
      options: { value: string; label: string }[],
      { inputValue }: { inputValue: string },
    ) => {
      const lower = inputValue.toLowerCase();
      return options.filter(({ value: id }) => {
        const project = ticketingProjectsOptions[id];
        if (!project) return false;
        return (
          project.id.toLowerCase().includes(lower) ||
          project.key.toLowerCase().includes(lower) ||
          project.name.toLowerCase().includes(lower)
        );
      });
    },
    [ticketingProjectsOptions],
  );

  const isServiceAccountInfoStep = step === DialogStep.serviceAccountInfo;
  const projectIdValue = watch('projectId');
  const nameValue = watch('name');

  const isValid =
    isServiceAccountInfoStep ||
    (!ticketingProjectsLoading &&
      !ticketingProjectsError &&
      !!projectIdValue &&
      !!nameValue);

  const handleFormSubmit = useMemo(
    () => handleSubmit((formData) => onSubmit(formData)),
    [handleSubmit, onSubmit],
  );

  const handleJiraStep = useMemo(
    () => handleSubmit(() => setStep(DialogStep.auth)),
    [handleSubmit],
  );

  useLayoutEffect(() => {
    onDialogProps({
      title: `${data?.id ? 'Update' : 'Connect'} ${toolLabel}`,
      isValid,
      onSubmit: isServiceAccountInfoStep ? handleJiraStep : handleFormSubmit,
      saveButtonText: isServiceAccountInfoStep
        ? 'Connect with Jira'
        : undefined,
      reset: resetForm,
      error: isServiceAccountInfoStep ? undefined : error,
      loading: isServiceAccountInfoStep ? false : loading,
    });
  }, [
    isValid,
    isServiceAccountInfoStep,
    toolLabel,
    data?.id,
    handleJiraStep,
    handleFormSubmit,
    resetForm,
    error,
    loading,
    onDialogProps,
  ]);

  return (
    <>
      {isServiceAccountInfoStep && (
        <JiraServiceAccountContent ctaLabel="Connect with Jira" />
      )}

      {(step === DialogStep.initial || step === DialogStep.auth) && <Loading />}

      {step === DialogStep.success && (
        <InputWrapper sx={{ gap: 3 }}>
          <TextField
            label="Name"
            {...register('name', { required: requiredLabel })}
            size="large"
          />
          <ErrorMessage>{errors.name?.message}</ErrorMessage>
          <Controller
            control={control}
            name="projectId"
            rules={{ required: requiredLabel }}
            render={({ field }: { field: any }) => (
              <Autocomplete
                {...field}
                value={
                  projectOptions.find(
                    (option) => option.value === field.value,
                  ) ??
                  field.value ??
                  null
                }
                options={projectOptions}
                getOptionLabel={getOptionLabel}
                filterOptions={filterOptions}
                isOptionEqualToValue={(option, value) =>
                  option.value ===
                  (typeof value === 'string' ? value : value?.value)
                }
                loadingText={`Loading ${projectLabel}s...`}
                loading={ticketingProjectsLoading}
                onChange={(_, value) => field.onChange(value?.value ?? value)}
                renderInput={(params: any) => (
                  <TextField
                    label={`Select ${toolLabel} ${projectLabel}`}
                    {...params}
                    size="large"
                  />
                )}
              />
            )}
          />
          <ErrorMessage>{errors.projectId?.message}</ErrorMessage>
        </InputWrapper>
      )}

      {!isServiceAccountInfoStep && (step === DialogStep.failure || error) && (
        <Alert
          severity="error"
          style={{ fontWeight: themeTokens.fontWeight.bold }}
        >
          Elementary {type} app failed to connect, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          for further assistance.
        </Alert>
      )}
    </>
  );
};

export default ToolDialogContent;
